import { useState } from 'react'
import { Button, Flex, Tooltip, Typography } from 'antd'
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { displayCode, isCodeSecret } from './voucherCode'
import type { VoucherState } from './voucherStockApi'

interface VoucherCodeTextProps {
  code: string
  state: VoucherState
  /** Partiya prefiksi — niqobda SHU QISM ochiq qoladi (`voucherCode.ts`) */
  prefix: string | null
}

/**
 * Kartaning kodi — sir bo'lgani BERKITILGAN holda chiziladi.
 *
 * <h2>⛔ OCHISH HOLATI HAR QATORDA O'ZINIKI VA U SAQLANMAYDI</h2>
 * `useState` komponentning ICHIDA: oyna yopilib qayta ochilsa hamma
 * kod yana berkitilgan holatga qaytadi. Holatni yuqoriga (sahifaga yoki
 * `localStorage` ga) ko'tarish «bir marta ochdim — endi doim ochiq»
 * degan xulqni berardi, ya'ni birinchi bosishdan keyin himoya butunlay
 * yo'qolardi.
 *
 * ⚠️ «HAMMASINI KO'RSATISH» TUGMASI ATAYLAB YO'Q. U butun jadvalni bir
 * bosishda ochib qo'yardi — aynan himoya qilinayotgan holat (yonidan
 * o'tgan odam ekranga qaraydi) esa aynan shunda yuzaga keladi. Kim
 * kartani sotayotgan bo'lsa, unga BITTA kod kerak.
 *
 * ⚠️ Sir bo'lmagan kod (`ACTIVATED`/`EXPIRED`/`VOIDED`) tugmasiz, oddiy
 * matn bo'lib turadi va NUSXA OLINADI: sherikning yagona ishlatish
 * holati — nizoni hal qilish, ya'ni kodni operatorga yuborish.
 */
export function VoucherCodeText({ code, state, prefix }: VoucherCodeTextProps) {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState(false)
  const secret = isCodeSecret(state)
  const shown = displayCode(code, state, prefix, revealed)

  if (!secret) {
    return (
      <Typography.Text copyable style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
        {code}
      </Typography.Text>
    )
  }

  return (
    <Flex align="center" gap={6}>
      {/*
        ⛔ BERKITILGAN QIYMATDA `copyable` YO'Q — `SubscriberCard` dagi
        niqoblangan telefon bilan AYNI sabab: nusxa olingan nuqtalar
        qatori hujjatda «karta kodi» bo'lib qolardi va uni hech kim
        tekshirmasdi. Ochilgandan keyin nusxa olish paydo bo'ladi.
      */}
      {revealed ? (
        <Typography.Text copyable style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
          {shown}
        </Typography.Text>
      ) : (
        <Typography.Text style={{ fontFamily: 'monospace', letterSpacing: 0.5 }}>
          {shown}
        </Typography.Text>
      )}
      <Tooltip title={revealed ? t('myVouchers.code.hide') : t('myVouchers.code.secretHint')}>
        <Button
          type="text"
          size="small"
          aria-label={revealed ? t('myVouchers.code.hide') : t('myVouchers.code.reveal')}
          icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => setRevealed((v) => !v)}
        />
      </Tooltip>
    </Flex>
  )
}
